import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import DurationUnitInput from '../UI/DurationUnitInput';
import {
   getDataPlan,
   updateDataPlan,
   deleteDataPlan,
   addLedgerEntry,
   addPlanRate,
   updatePlanRate,
   removePlanRate,
   linkDeviceToPlan,
   updateDeviceAllotment,
   unlinkDeviceFromPlan,
   formatMb,
   planRemainingMb,
   planTotalMb,
   DataPlan,
   DataPlanMode,
   LedgerEntryType,
} from '@/api/dataPlans';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { listTransfersApi } from '@/api/vendo';
import { dataPlanPickBlock, sellsFromOwnSlice } from './dataPlanPick';
import {
   isPickableDevice,
   buildPendingTransferIds,
} from '@/utils/deviceFilters';

interface Props {
   planId: string;
   onClose: () => void;
}

type Tab = 'overview' | 'vendos' | 'rates' | 'ledger';
type DeleteState = 'idle' | 'confirming' | 'deleting';

// Per-plan management modal. Tabs (Overview / Vendos / Rates / Ledger)
// keep height bounded on small laptop screens — same shape as
// DeviceSettings. All colors come from theme tokens (CSS-var-backed)
// so dark mode works.
function DataPlanModal({ planId, onClose }: Props) {
   const qc = useQueryClient();
   const [tab, setTab] = useState<Tab>('overview');

   const { data: plan, isLoading } = useQuery({
      queryKey: ['data-plan', planId],
      queryFn: () => getDataPlan(planId),
   });

   if (isLoading || !plan) {
      return (
         <Backdrop onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
               <SkeletonLines lines={3} />
            </Modal>
         </Backdrop>
      );
   }

   const refresh = () => qc.invalidateQueries({ queryKey: ['data-plan', planId] });

   return (
      <Backdrop onClick={onClose}>
         <Modal onClick={(e) => e.stopPropagation()}>
            <Summary>
               <SummaryLeft>
                  <PlanName>{plan.name}</PlanName>
                  {plan.isp && <PlanIsp>{plan.isp}</PlanIsp>}
                  <ModePill $mode={plan.mode}>
                     {plan.mode === 'shared' ? 'Shared' : 'Manual'}
                  </ModePill>
               </SummaryLeft>
               <CloseX onClick={onClose} aria-label="Close">×</CloseX>
            </Summary>

            <TabBar>
               {([
                  { key: 'overview', label: 'Overview' },
                  { key: 'vendos', label: 'Vendos', count: plan.devices.length },
                  { key: 'rates', label: 'Rates', count: plan.rates.length },
                  { key: 'ledger', label: 'Ledger', count: plan.ledger.length },
               ] as { key: Tab; label: string; count?: number }[]).map((t) => (
                  <TabItem
                     key={t.key}
                     $active={tab === t.key}
                     onClick={() => setTab(t.key)}
                  >
                     {t.label}
                     {t.count !== undefined && <TabCount>{t.count}</TabCount>}
                  </TabItem>
               ))}
            </TabBar>

            <TabBody>
               {tab === 'overview' && (
                  <OverviewPanel
                     plan={plan}
                     onSaved={() => { refresh(); qc.invalidateQueries({ queryKey: ['data-plans'] }); }}
                     onDeleted={() => { qc.invalidateQueries({ queryKey: ['data-plans'] }); onClose(); }}
                  />
               )}
               {tab === 'vendos' && <VendosPanel plan={plan} onChange={refresh} />}
               {tab === 'rates' && <RatesPanel plan={plan} onChange={refresh} />}
               {tab === 'ledger' && <LedgerPanel plan={plan} onChange={refresh} />}
            </TabBody>
         </Modal>
      </Backdrop>
   );
}

// ---- Overview ----

function OverviewPanel({
   plan, onSaved, onDeleted,
}: { plan: DataPlan; onSaved: () => void; onDeleted: () => void }) {
   const [name, setName] = useState(plan.name);
   const [isp, setIsp] = useState(plan.isp);
   const [mode, setMode] = useState<DataPlanMode>(plan.mode);
   const [pesoBaseMbStr, setPesoBaseMbStr] = useState(String(plan.pesoBaseMb ?? 100));
   const [deleteState, setDeleteState] = useState<DeleteState>('idle');
   const pesoBaseMbNum = Number(pesoBaseMbStr);
   const pesoBaseMbValid = Number.isFinite(pesoBaseMbNum) && pesoBaseMbNum >= 0;
   const dirty =
      name !== plan.name
      || isp !== plan.isp
      || mode !== plan.mode
      || (pesoBaseMbValid && pesoBaseMbNum !== (plan.pesoBaseMb ?? 100));

   const total = planTotalMb(plan);
   const remaining = planRemainingMb(plan);
   const usedPct = total > 0 ? Math.min(100, (plan.totalUsedMb / total) * 100) : 0;
   const lowPool = total > 0 && usedPct >= 85;

   const save = useMutation({
      mutationFn: () => updateDataPlan(plan._id, {
         name, isp, mode,
         ...(pesoBaseMbValid ? { pesoBaseMb: pesoBaseMbNum } : {}),
      }),
      onSuccess: () => { toast.success('Plan updated'); onSaved(); },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not save'),
   });

   const remove = useMutation({
      mutationFn: () => deleteDataPlan(plan._id),
      onSuccess: () => { toast.success(`Deleted "${plan.name}"`, { autoClose: 2000 }); onDeleted(); },
      onError: (err: any) => {
         toast.error(err?.response?.data?.error || 'Could not delete plan');
         setDeleteState('idle');
      },
   });

   return (
      <Panel>
         <PoolCard>
            <PoolStats>
               <PoolStat>
                  <PoolValue>{formatMb(plan.totalUsedMb)}</PoolValue>
                  <PoolLbl>used</PoolLbl>
               </PoolStat>
               <PoolStat>
                  <PoolValue $accent={!lowPool}>{formatMb(remaining)}</PoolValue>
                  <PoolLbl>remaining</PoolLbl>
                  {(plan.returnedMb ?? 0) > 0 && (
                     <PoolReturned title="Data customers bought but did not use before their session ended. It is back in the pool and counted in Remaining.">
                        includes {formatMb(plan.returnedMb ?? 0)} returned from ended sessions
                     </PoolReturned>
                  )}
               </PoolStat>
               <PoolStat>
                  <PoolValue>{formatMb(total)}</PoolValue>
                  <PoolLbl>total</PoolLbl>
               </PoolStat>
            </PoolStats>
            <PoolBar>
               <PoolBarFill $pct={usedPct} $low={lowPool} />
            </PoolBar>
         </PoolCard>

         <Field>
            <Lbl>Plan name</Lbl>
            <Inp value={name} onChange={(e) => setName(e.target.value)} />
         </Field>
         <FieldRow>
            <Field $flex={1}>
               <Lbl>ISP</Lbl>
               <Inp value={isp} onChange={(e) => setIsp(e.target.value)} placeholder="e.g. Starlink" />
            </Field>
            <Field $flex={1}>
               <Lbl>Pool mode</Lbl>
               <ModeRow>
                  <ModeOpt $active={mode === 'shared'} onClick={() => setMode('shared')}>Shared</ModeOpt>
                  <ModeOpt $active={mode === 'manual'} onClick={() => setMode('manual')}>Manual</ModeOpt>
               </ModeRow>
            </Field>
         </FieldRow>

         <Field>
            <Lbl>Base value per ₱1 (MB)</Lbl>
            <Inp
               type="number"
               min={0}
               value={pesoBaseMbStr}
               onChange={(e) => setPesoBaseMbStr(e.target.value)}
               placeholder="100"
            />
            <Hint>
               How much data ₱1 buys when a customer drops an amount that doesn't
               match a configured rate. Example: rates are P5, P10, P20; a customer
               drops ₱3 — they get 3 × this value. Also added on top of the matched
               bundle when a customer overshoots (e.g. selects P5, drops ₱6 → P5
               bundle + 1 × this value). Default 100.
            </Hint>
         </Field>

         {dirty && (
            <SaveRow>
               <SaveBtn onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save changes'}
               </SaveBtn>
            </SaveRow>
         )}

         <DangerZone>
            <DangerLbl>Danger zone</DangerLbl>
            <DangerText>
               Deleting a plan is permanent. The server refuses if any vendos are still
               linked — unlink them first from the Vendos tab.
            </DangerText>
            {deleteState === 'idle' && (
               <DangerBtn onClick={() => setDeleteState('confirming')}>
                  Delete this plan
               </DangerBtn>
            )}
            {deleteState === 'confirming' && (
               <ConfirmRow>
                  <ConfirmText>Delete <strong>{plan.name}</strong>?</ConfirmText>
                  <DangerBtn onClick={() => { setDeleteState('deleting'); remove.mutate(); }}>
                     Yes, delete
                  </DangerBtn>
                  <CancelLink type="button" onClick={() => setDeleteState('idle')}>Cancel</CancelLink>
               </ConfirmRow>
            )}
            {deleteState === 'deleting' && <DangerBtn disabled>Deleting…</DangerBtn>}
         </DangerZone>
      </Panel>
   );
}

// ---- Vendos ----

function VendosPanel({ plan, onChange }: { plan: DataPlan; onChange: () => void }) {
   const fetchDevices = useVendoDevicesStore((s) => s.fetch);
   const allDevices = useVendoDevicesStore((s) => s.devices);
   const fetched = useVendoDevicesStore((s) => s.fetched);
   const [drawerOpen, setDrawerOpen] = useState(false);

   useEffect(() => { if (!fetched) fetchDevices(); }, [fetched, fetchDevices]);

   // Pending outgoing transfers — devices whose ownership is mid-handoff
   // shouldn't accept new plan links from the seller. React Query caches
   // this for the picker session and the dashboard already pre-warms it
   // in other places, so usually a no-op fetch.
   const { data: outgoingTransfers } = useQuery({
      queryKey: ['transfers', 'outgoing'],
      queryFn: () => listTransfersApi('outgoing'),
   });
   const pendingTransferIds = buildPendingTransferIds(outgoingTransfers);

   // Filter out null `d.device` — happens when a linked vendo was deleted
   // but the data plan still holds the reference (cleanup hasn't run, or
   // delete handler didn't unlink). Without this guard, .map crashes on
   // `null._id` and the whole modal breaks.
   const linkedIds = new Set(
      plan.devices
         .map((d) =>
            d.device == null
               ? null
               : typeof d.device === 'string'
               ? d.device
               : d.device._id
         )
         .filter((id): id is string => id != null)
   );
   // Pickable = not already linked to THIS plan, not on ANOTHER plan, AND
   // passes the shared "owned + setup-done + no pending transfer" filter.
   // A Ruijie / OrangePi on firmware older than 2.6.0 stays in the list but
   // BLOCKED, with the reason — hiding it made operators ask why their vendo
   // was missing. The server refuses the link too (dataPlanPickBlock).
   const pickable = allDevices
      .filter(
         (d) =>
            !linkedIds.has(d._id) &&
            !d.activeDataPlan &&
            isPickableDevice(d, pendingTransferIds)
      )
      .map((d) => ({
         _id: d._id,
         name: d.name,
         deviceId: d.deviceId,
         blocked: dataPlanPickBlock(d),
      }));

   const total = planTotalMb(plan);

   const unlink = useMutation({
      mutationFn: (deviceId: string) => unlinkDeviceFromPlan(plan._id, deviceId),
      onSuccess: () => { onChange(); toast.success('Vendo unlinked'); },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not unlink'),
   });

   return (
      <Panel>
         {plan.devices.length === 0 ? (
            <PanelHint>No vendos linked yet. Tap "Link Vendo" below to start sharing this plan.</PanelHint>
         ) : (
            <DevList>
               {plan.devices.map((link) => {
                  // link.device may be null if the underlying vendo was
                  // deleted but the link wasn't cleaned up. Render a stub
                  // row so the operator can still see + unlink it.
                  const dev =
                     link.device == null
                        ? { _id: '', name: '(deleted vendo)', deviceId: '' }
                        : typeof link.device === 'string'
                        ? { _id: link.device, name: link.device, deviceId: link.device }
                        : link.device;
                  return (
                     <DevRow
                        key={link._id}
                        plan={plan}
                        deviceId={dev._id}
                        deviceName={dev.name}
                        ownSlice={sellsFromOwnSlice(allDevices.find((d) => d._id === dev._id) ?? {})}
                        allotmentMb={link.allotmentMb}
                        usedMb={link.usedMb}
                        poolTotalMb={total}
                        onChange={onChange}
                        onUnlink={() => unlink.mutate(dev._id)}
                     />
                  );
               })}
            </DevList>
         )}
         <LinkVendoBtn onClick={() => setDrawerOpen(true)} disabled={pickable.length === 0}>
            + Link Vendo
            {pickable.length === 0 && <span> (no available vendos)</span>}
         </LinkVendoBtn>

         {drawerOpen && (
            <LinkVendoDrawer
               planId={plan._id}
               pickable={pickable}
               onClose={() => setDrawerOpen(false)}
               onLinked={() => { onChange(); setDrawerOpen(false); }}
            />
         )}
      </Panel>
   );
}

export function LinkVendoDrawer({
   planId, pickable, onClose, onLinked,
}: {
   planId: string;
   pickable: { _id: string; name: string; deviceId: string; blocked: string | null }[];
   onClose: () => void;
   onLinked: () => void;
}) {
   const [selected, setSelected] = useState<Set<string>>(new Set());
   const [submitting, setSubmitting] = useState(false);

   const toggle = (id: string) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id); else next.add(id);
         return next;
      });
   };

   const handleLink = async () => {
      if (selected.size === 0) return;
      setSubmitting(true);
      const ids = Array.from(selected);
      let succeeded = 0;
      let failed: string[] = [];
      let firstReason = '';
      // Server doesn't have a bulk-link endpoint; iterate and collect
      // results so partial success still updates the UI cleanly.
      for (const id of ids) {
         try {
            await linkDeviceToPlan(planId, { deviceId: id });
            succeeded++;
         } catch (err: any) {
            const name = pickable.find((d) => d._id === id)?.name || id;
            failed.push(name);
            // The server says WHY ("set the plan to Manual first", "needs
            // firmware 2.6.0"); a bare name list left the operator guessing.
            if (!firstReason) firstReason = err?.response?.data?.error || '';
         }
      }
      setSubmitting(false);
      if (succeeded > 0) {
         toast.success(`Linked ${succeeded} vendo${succeeded === 1 ? '' : 's'}`);
      }
      if (failed.length > 0) {
         toast.error(`Could not link: ${failed.join(', ')}${firstReason ? ` — ${firstReason}` : ''}`);
      }
      onLinked();
   };

   return (
      <DrawerBackdrop onClick={onClose}>
         <Drawer onClick={(e) => e.stopPropagation()}>
            <DrawerHead>
               <DrawerTitle>Link vendos to this plan</DrawerTitle>
               <CloseX onClick={onClose}>×</CloseX>
            </DrawerHead>
            <DrawerSub>
               Pick one or more vendos to add. Each will switch to data-rate mode
               and inherit this plan's rates.
            </DrawerSub>

            <DrawerList>
               {pickable.length === 0 ? (
                  <PanelHint>No vendos available. All your vendos are either already linked here or to another plan.</PanelHint>
               ) : (
                  pickable.map((d) => (
                     <DrawerRow
                        key={d._id}
                        onClick={() => { if (!d.blocked) toggle(d._id); }}
                        aria-disabled={!!d.blocked}
                        style={d.blocked ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                     >
                        <Checkbox $checked={selected.has(d._id)}>
                           {selected.has(d._id) && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                 <polyline points="20 6 9 17 4 12" />
                              </svg>
                           )}
                        </Checkbox>
                        <DrawerRowBody>
                           <DrawerRowName>{d.name}</DrawerRowName>
                           <DrawerRowId>{d.deviceId}</DrawerRowId>
                           {d.blocked && <DrawerRowId>{d.blocked}</DrawerRowId>}
                        </DrawerRowBody>
                     </DrawerRow>
                  ))
               )}
            </DrawerList>

            <DrawerFoot>
               <DrawerCancel onClick={onClose} disabled={submitting}>Cancel</DrawerCancel>
               <DrawerLink onClick={handleLink} disabled={selected.size === 0 || submitting}>
                  {submitting
                     ? 'Linking…'
                     : `Link ${selected.size || ''} ${selected.size === 1 ? 'vendo' : 'vendos'}`.trim()}
               </DrawerLink>
            </DrawerFoot>
         </Drawer>
      </DrawerBackdrop>
   );
}

function DevRow({
   plan, deviceId, deviceName, ownSlice, allotmentMb, usedMb, poolTotalMb, onChange, onUnlink,
}: {
   // A Ruijie / OrangePi sells only from its own allotment (it keeps selling
   // with the cloud unreachable, so it can never draw from a shared pool).
   // With no allotment it sells NOTHING — say so on the row.
   ownSlice: boolean;
   plan: DataPlan;
   deviceId: string;
   deviceName: string;
   allotmentMb: number | null;
   usedMb: number;
   poolTotalMb: number;
   onChange: () => void;
   onUnlink: () => void;
}) {
   // Plan-level units are DECIMAL (1 GB = 1000 MB), matching telecom /
   // consumer marketing convention. Don't switch back to /1024 here —
   // 0.10 GB then becomes 102 MB instead of 100, which confused operators.
   // The internal bytes conversion at the router layer still uses 1024
   // (capMb * 1024 * 1024) — that's a router-enforcement detail; the
   // small overshoot is a feature (customers get a little more), not a
   // bug. Display/input here is the customer-facing surface that needs
   // to match the number the operator typed.
   const [allotGb, setAllotGb] = useState(allotmentMb ? (allotmentMb / 1000).toFixed(2) : '');

   useEffect(() => {
      setAllotGb(allotmentMb ? (allotmentMb / 1000).toFixed(2) : '');
   }, [allotmentMb]);

   const update = useMutation({
      mutationFn: (mb: number | null) => updateDeviceAllotment(plan._id, deviceId, mb),
      onSuccess: onChange,
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not update allotment'),
   });

   const commit = () => {
      const gb = parseFloat(allotGb);
      const mb = Number.isFinite(gb) && gb > 0 ? Math.round(gb * 1000) : null;
      if (mb !== allotmentMb) update.mutate(mb);
   };

   const denominator = plan.mode === 'manual' && allotmentMb ? allotmentMb : poolTotalMb;
   const usedPct = denominator > 0 ? Math.min(100, (usedMb / denominator) * 100) : 0;

   return (
      <DevRowWrap>
         <DevHeader>
            <DevName>{deviceName}</DevName>
            <DevUnlink onClick={onUnlink} title="Unlink vendo">Unlink</DevUnlink>
         </DevHeader>
         <DevBody>
            {plan.mode === 'manual' ? (
               <AllotInputWrap>
                  <AllotInput
                     value={allotGb}
                     onChange={(e) => setAllotGb(e.target.value)}
                     onBlur={commit}
                     onKeyDown={(e) => e.key === 'Enter' && commit()}
                     placeholder="0"
                     type="text"
                     inputMode="decimal"
                  />
                  <AllotUnit>GB allotted</AllotUnit>
               </AllotInputWrap>
            ) : (
               <AllotShared>Shared from pool</AllotShared>
            )}
            {ownSlice && plan.mode === 'manual' && !allotmentMb && (
               <PanelHint>Set an allotment, for example 5 — this vendo sells nothing until it has one.</PanelHint>
            )}
            {ownSlice && plan.mode !== 'manual' && (
               <PanelHint>This vendo sells only from its own allotment. Switch the plan to Manual and set one.</PanelHint>
            )}
            <DevBar>
               <DevBarFill $pct={usedPct} />
            </DevBar>
            <DevUsage>
               {formatMb(usedMb)} used
               {plan.mode === 'manual' && allotmentMb && ` of ${formatMb(allotmentMb)}`}
            </DevUsage>
         </DevBody>
      </DevRowWrap>
   );
}

// ---- Rates ----

function RatesPanel({ plan, onChange }: { plan: DataPlan; onChange: () => void }) {
   const [adding, setAdding] = useState(false);
   const [tab, setTab] = useState<'coin' | 'voucher'>('coin');
   const remove = useMutation({
      mutationFn: (rateId: string) => removePlanRate(plan._id, rateId),
      onSuccess: () => { onChange(); toast.success('Rate removed'); },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not remove'),
   });

   const coinRates = plan.rates.filter((r) => (r.method ?? 'coin') === 'coin');
   const voucherRates = plan.rates.filter((r) => r.method === 'voucher');
   const visibleRates = tab === 'coin' ? coinRates : voucherRates;

   return (
      <Panel>
         <PlanRateTabBar>
            <PlanRateTab $active={tab === 'coin'} onClick={() => { setTab('coin'); setAdding(false); }}>
               Coin Rates <PlanRateTabCount>{coinRates.length}</PlanRateTabCount>
            </PlanRateTab>
            <PlanRateTab $active={tab === 'voucher'} onClick={() => { setTab('voucher'); setAdding(false); }}>
               Voucher Rates <PlanRateTabCount>{voucherRates.length}</PlanRateTabCount>
            </PlanRateTab>
         </PlanRateTabBar>
         {visibleRates.length === 0 && !adding && (
            <PanelHint>
               {tab === 'coin'
                  ? 'No coin rates yet. Add at least one so customers see a coin option on the captive portal.'
                  : 'No voucher rates yet. Add one to bulk-print data vouchers or sell them via GCash. If you leave this empty, the device falls back to its time-based voucher rates.'}
            </PanelHint>
         )}
         {visibleRates.length > 0 && (
            <RateList>
               <RateHead>
                  <RateHeadCell $w="80px">Price (₱)</RateHeadCell>
                  <RateHeadCell $w="80px">Cap (GB)</RateHeadCell>
                  <RateHeadCell $w="180px">Validity</RateHeadCell>
                  <RateHeadCell>Label</RateHeadCell>
                  <RateHeadCell $w="32px"></RateHeadCell>
               </RateHead>
               {visibleRates.map((r) => (
                  <EditableRateRow
                     key={r._id}
                     planId={plan._id}
                     rateId={r._id}
                     price={r.price}
                     capMb={r.capMb}
                     label={r.label}
                     validityMinutes={r.validityMinutes ?? null}
                     onChanged={onChange}
                     onRemove={() => remove.mutate(r._id)}
                  />
               ))}
            </RateList>
         )}
         {adding ? (
            <AddRateForm
               planId={plan._id}
               method={tab}
               onDone={() => { setAdding(false); onChange(); }}
               onCancel={() => setAdding(false)}
            />
         ) : (
            <AddBtn onClick={() => setAdding(true)}>+ Add {tab} rate</AddBtn>
         )}
      </Panel>
   );
}

// Inline-editable rate row. Each cell holds a local string so the
// owner can type without server round-trips per keystroke. On blur
// (or Enter), if any field changed and is valid, fire updatePlanRate
// — server returns the refreshed plan and parent's invalidate sweep
// re-renders. Failed PATCH reverts the local state to the original
// value so the row stays consistent with the server.
function EditableRateRow({
   planId, rateId, price, capMb, label, validityMinutes, onChanged, onRemove,
}: {
   planId: string;
   rateId: string;
   price: number;
   capMb: number;
   label: string;
   validityMinutes: number | null;
   onChanged: () => void;
   onRemove: () => void;
}) {
   const initialCapGb = (capMb / 1000).toFixed(capMb % 1000 === 0 ? 0 : 2);
   const [priceStr, setPriceStr] = useState(String(price));
   const [capGbStr, setCapGbStr] = useState(initialCapGb);
   const [labelStr, setLabelStr] = useState(label);

   useEffect(() => { setPriceStr(String(price)); }, [price]);
   useEffect(() => { setCapGbStr(initialCapGb); /* eslint-disable-next-line */ }, [capMb]);
   useEffect(() => { setLabelStr(label); }, [label]);

   const patch = useMutation({
      mutationFn: (
         payload: Partial<{
            price: number; capMb: number; label: string;
            validityMinutes: number | null;
         }>
      ) => updatePlanRate(planId, rateId, payload),
      onSuccess: () => onChanged(),
      onError: (err: any) => {
         toast.error(err?.response?.data?.error || 'Could not save rate');
         // Revert on failure so display matches server state.
         setPriceStr(String(price));
         setCapGbStr(initialCapGb);
         setLabelStr(label);
      },
   });

   const commitPrice = () => {
      const next = Number(priceStr);
      if (!Number.isFinite(next) || next < 0) { setPriceStr(String(price)); return; }
      if (next !== price) patch.mutate({ price: next });
   };
   const commitCap = () => {
      const gb = Number(capGbStr);
      if (!Number.isFinite(gb) || gb <= 0) { setCapGbStr(initialCapGb); return; }
      const mb = Math.round(gb * 1000);
      if (mb !== capMb) patch.mutate({ capMb: mb });
   };
   const commitLabel = () => {
      const trimmed = labelStr.trim();
      if (!trimmed) { setLabelStr(label); return; }
      if (trimmed !== label) patch.mutate({ label: trimmed });
   };

   const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, commit: () => void) => {
      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      if (e.key === 'Escape') {
         setPriceStr(String(price));
         setCapGbStr(initialCapGb);
         setLabelStr(label);
         (e.target as HTMLInputElement).blur();
      }
   };

   return (
      <RateRow>
         <CompactInp
            $w="80px"
            type="number"
            value={priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            onBlur={commitPrice}
            onKeyDown={(e) => onKeyDown(e, commitPrice)}
         />
         <CompactInp
            $w="80px"
            type="number"
            step="0.1"
            value={capGbStr}
            onChange={(e) => setCapGbStr(e.target.value)}
            onBlur={commitCap}
            onKeyDown={(e) => onKeyDown(e, commitCap)}
         />
         <ValidityCell>
            <DurationUnitInput
               value={validityMinutes}
               onChange={(mins) => {
                  if ((mins ?? null) !== (validityMinutes ?? null)) {
                     patch.mutate({ validityMinutes: mins });
                  }
               }}
               allowDefault
               min={0}
               compact
            />
         </ValidityCell>
         <CompactInp
            value={labelStr}
            onChange={(e) => setLabelStr(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => onKeyDown(e, commitLabel)}
         />
         <RateRm onClick={onRemove} title="Remove rate">×</RateRm>
      </RateRow>
   );
}

function AddRateForm({
   planId, method, onDone, onCancel,
}: {
   planId: string;
   method: 'coin' | 'voucher';
   onDone: () => void;
   onCancel: () => void;
}) {
   const [price, setPrice] = useState('');
   const [capGb, setCapGb] = useState('');
   const [label, setLabel] = useState('');
   const [validityMinutes, setValidityMinutes] = useState<number | null>(null);

   const create = useMutation({
      mutationFn: () => addPlanRate(planId, {
         price: Number(price),
         capMb: Math.round(Number(capGb) * 1000),
         label: label.trim(),
         method,
         validityMinutes,
      }),
      onSuccess: () => { toast.success('Rate added'); onDone(); },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not add rate'),
   });

   const valid = Number(price) >= 0 && Number(capGb) > 0 && label.trim().length > 0;

   return (
      <AddForm>
         <AddRow>
            <CompactInp $w="80px" type="number" placeholder="Price ₱" value={price} onChange={(e) => setPrice(e.target.value)} />
            <CompactInp $w="80px" type="number" step="0.1" placeholder="GB" value={capGb} onChange={(e) => setCapGb(e.target.value)} />
            <ValidityCell>
               <DurationUnitInput
                  value={validityMinutes}
                  onChange={setValidityMinutes}
                  allowDefault
                  min={0}
                  compact
               />
            </ValidityCell>
            <CompactInp placeholder="Label e.g. 3GB Plan" value={label} onChange={(e) => setLabel(e.target.value)} />
         </AddRow>
         <AddBtnRow>
            <AddCancel onClick={onCancel}>Cancel</AddCancel>
            <AddSubmit onClick={() => create.mutate()} disabled={!valid || create.isPending}>
               {create.isPending ? 'Adding…' : 'Add rate'}
            </AddSubmit>
         </AddBtnRow>
      </AddForm>
   );
}

// ---- Ledger ----

function LedgerPanel({ plan, onChange }: { plan: DataPlan; onChange: () => void }) {
   const [adding, setAdding] = useState(false);
   const sorted = useMemo(
      () => [...plan.ledger].sort((a, b) => +new Date(b.date) - +new Date(a.date)),
      [plan.ledger]
   );

   return (
      <Panel>
         {sorted.length === 0 && !adding && (
            <PanelHint>
               No entries yet. Add a base entry for your monthly quota or a top-up to start the pool.
            </PanelHint>
         )}
         {sorted.length > 0 && (
            <LedgerList>
               {sorted.map((e) => (
                  <LedgerRow key={e._id}>
                     <LedgerDate>{formatLedgerDate(e.date)}</LedgerDate>
                     <LedgerType $type={e.type}>{e.type}</LedgerType>
                     <LedgerAmount $negative={e.amountMb < 0}>
                        {e.amountMb < 0 ? '−' : '+'}{formatMb(Math.abs(e.amountMb))}
                     </LedgerAmount>
                     <LedgerNote>{e.note || ''}</LedgerNote>
                  </LedgerRow>
               ))}
            </LedgerList>
         )}
         {adding ? (
            <AddLedgerForm planId={plan._id} onDone={() => { setAdding(false); onChange(); }} onCancel={() => setAdding(false)} />
         ) : (
            <AddBtn onClick={() => setAdding(true)}>+ Add entry</AddBtn>
         )}
      </Panel>
   );
}

function AddLedgerForm({ planId, onDone, onCancel }: { planId: string; onDone: () => void; onCancel: () => void }) {
   const [type, setType] = useState<LedgerEntryType>('topup');
   const [amountGb, setAmountGb] = useState('');
   const [note, setNote] = useState('');

   const create = useMutation({
      mutationFn: () => addLedgerEntry(planId, {
         type,
         amountMb: Math.round(Number(amountGb) * 1000),
         note: note.trim() || undefined,
      }),
      onSuccess: () => { toast.success('Entry added'); onDone(); },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not add entry'),
   });

   const num = Number(amountGb);
   const valid = Number.isFinite(num) && num !== 0
      && (type === 'adjustment' || num > 0);

   return (
      <AddForm>
         <LedgerTypeRow>
            <LedgerTypeOpt $active={type === 'base'} onClick={() => setType('base')}>Base</LedgerTypeOpt>
            <LedgerTypeOpt $active={type === 'topup'} onClick={() => setType('topup')}>Top-up</LedgerTypeOpt>
            <LedgerTypeOpt $active={type === 'adjustment'} onClick={() => setType('adjustment')}>Adjustment</LedgerTypeOpt>
         </LedgerTypeRow>
         <AddRow>
            <CompactInp
               $w="100px"
               type="number"
               step="0.1"
               placeholder={type === 'adjustment' ? '±GB' : 'GB'}
               value={amountGb}
               onChange={(e) => setAmountGb(e.target.value)}
            />
            <CompactInp
               placeholder={type === 'base' ? 'e.g. April monthly' : type === 'topup' ? 'e.g. 50GB top-up' : 'Reason for correction'}
               value={note}
               onChange={(e) => setNote(e.target.value)}
            />
         </AddRow>
         <AddBtnRow>
            <AddCancel onClick={onCancel}>Cancel</AddCancel>
            <AddSubmit onClick={() => create.mutate()} disabled={!valid || create.isPending}>
               {create.isPending ? 'Adding…' : 'Add entry'}
            </AddSubmit>
         </AddBtnRow>
      </AddForm>
   );
}

function formatLedgerDate(iso: string): string {
   return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
   });
}

export default DataPlanModal;

// ---- Outer modal shell (mirrors DeviceSettings sizing) ----

const Backdrop = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0,0,0,0.5);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 100;
   padding: 20px;
`;

const Modal = styled.div`
   background: var(--app-surface, #fff);
   border: 1px solid ${colors.GRAY12};
   border-radius: 14px;
   padding: 18px 22px 22px;
   width: 720px;
   max-width: 100%;
   /* Fixed, not max: the tabs differ a lot in length (Overview is long,
      Ledger is two rows) and a modal that shrinks and grows as you click
      tabs is jarring. The body scrolls inside instead (Ray 2026-09-15). */
   height: 78vh;
   display: flex;
   flex-direction: column;
   gap: 10px;
   @media (max-width: 768px) { width: 100%; }
`;


const Summary = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   padding: 8px 12px;
   border-radius: 8px;
   background: var(--app-brand-tint);
`;

const SummaryLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

const PlanName = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const PlanIsp = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
`;

const ModePill = styled.span<{ $mode: DataPlanMode }>`
   font-size: 9px;
   font-weight: 700;
   padding: 2px 8px;
   border-radius: 999px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${(p) => p.$mode === 'shared' ? `${colors.PRIMARY}15` : `${colors.SUCCESS}15`};
   color: ${(p) => p.$mode === 'shared' ? colors.PRIMARY : colors.SUCCESS};
`;

const CloseX = styled.button`
   font-size: 22px;
   line-height: 1;
   color: ${colors.GRAY3};
   background: none;
   border: none;
   cursor: pointer;
   padding: 0 4px;
   &:hover { color: ${colors.MAIN_DARK}; }
`;

const TabBar = styled.div`
   display: flex;
   gap: 0;
   border-bottom: 1px solid ${colors.GRAY12};
   overflow-x: auto;
`;

const TabItem = styled.button<{ $active: boolean }>`
   background: none;
   border: none;
   padding: 8px 14px;
   font-size: 13px;
   font-weight: ${({ $active }) => ($active ? 600 : 400)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   white-space: nowrap;
   display: flex;
   align-items: center;
   gap: 6px;
   transition: all 0.15s;
   margin-bottom: -1px;
   &:hover { color: ${colors.PRIMARY}; }
`;

const TabCount = styled.span`
   font-size: 10px;
   font-weight: 700;
   background: ${colors.GRAY14};
   color: ${colors.GRAY3};
   padding: 1px 6px;
   border-radius: 10px;
   min-width: 18px;
   text-align: center;
`;

const TabBody = styled.div`
   overflow-y: auto;
   flex: 1 1 auto;
   min-height: 0;
   padding-top: 4px;
`;

const Panel = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const PanelHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   padding: 10px 12px;
   background: ${colors.GRAY14};
   border-radius: 6px;
`;

// ---- Pool overview ----

const PoolCard = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 14px 16px;
   background: var(--app-surface-muted, transparent);
`;

const PoolStats = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 12px;
   margin-bottom: 12px;
`;

const PoolStat = styled.div`
   text-align: left;
`;

const PoolValue = styled.div<{ $accent?: boolean }>`
   font-size: 18px;
   font-weight: 700;
   color: ${(p) => p.$accent ? colors.PRIMARY : colors.MAIN_DARK};
`;

const PoolReturned = styled.div`
   margin-top: 4px;
   font-size: 11px;
   color: ${colors.GRAY6};
`;

const PoolLbl = styled.div`
   font-size: 10px;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin-top: 2px;
`;

const PoolBar = styled.div`
   height: 8px;
   background: ${colors.GRAY14};
   border-radius: 4px;
   overflow: hidden;
`;

const PoolBarFill = styled.div<{ $pct: number; $low: boolean }>`
   height: 100%;
   width: ${(p) => p.$pct}%;
   background: ${(p) => p.$low ? colors.DANGER : colors.PRIMARY};
   border-radius: 4px;
   transition: width 0.4s ease-out;
`;

// ---- Form fields ----

const FieldRow = styled.div`
   display: flex;
   gap: 12px;
`;

const Field = styled.div<{ $flex?: number }>`
   flex: ${(p) => p.$flex || 'unset'};
`;

const Lbl = styled.label`
   display: block;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   margin-bottom: 5px;
`;

const Inp = styled.input`
   width: 100%;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 7px;
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface, #fff);
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;

const Hint = styled.p`
   margin: 6px 0 0;
   font-size: 11px;
   line-height: 1.45;
   color: ${colors.GRAY8};
`;

const ModeRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 6px;
`;

const ModeOpt = styled.button<{ $active: boolean }>`
   padding: 9px;
   border-radius: 7px;
   border: 1.5px solid ${(p) => p.$active ? colors.PRIMARY : colors.GRAY12};
   background: ${(p) => p.$active ? `${colors.PRIMARY}10` : 'transparent'};
   color: ${colors.MAIN_DARK};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

const SaveRow = styled.div`
   display: flex;
   justify-content: flex-end;
`;

const SaveBtn = styled.button`
   padding: 8px 18px;
   border-radius: 7px;
   background: ${colors.PRIMARY};
   border: none;
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: opacity 0.15s;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ---- Danger zone ----

const DangerZone = styled.div`
   margin-top: 16px;
   padding: 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: ${colors.GRAY14};
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const DangerLbl = styled.div`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.DANGER};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const DangerText = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const DangerBtn = styled.button`
   align-self: flex-start;
   font-size: 11px;
   font-weight: 700;
   padding: 6px 12px;
   background: ${colors.DANGER};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ConfirmRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

const ConfirmText = styled.span`
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const CancelLink = styled.button`
   background: none;
   border: none;
   color: ${colors.GRAY3};
   font-size: 11px;
   text-decoration: underline;
   cursor: pointer;
`;

// ---- Vendos panel ----

const DevList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const DevRowWrap = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 12px 14px;
`;

const DevHeader = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 8px;
`;

const DevName = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const DevUnlink = styled.button`
   font-size: 11px;
   color: ${colors.DANGER};
   background: none;
   border: none;
   cursor: pointer;
   padding: 2px 6px;
   border-radius: 4px;
   &:hover { background: ${colors.DANGER}15; }
`;

const DevBody = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
`;

const AllotInputWrap = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   min-width: 140px;
`;

const AllotInput = styled.input`
   width: 70px;
   padding: 6px 8px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 12px;
   text-align: right;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface, #fff);
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;

const AllotUnit = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
`;

const AllotShared = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   font-style: italic;
   min-width: 140px;
`;

const DevBar = styled.div`
   flex: 1;
   height: 8px;
   background: ${colors.GRAY14};
   border-radius: 4px;
   overflow: hidden;
`;

const DevBarFill = styled.div<{ $pct: number }>`
   height: 100%;
   width: ${(p) => p.$pct}%;
   background: ${colors.PRIMARY};
   border-radius: 4px;
   transition: width 0.4s ease-out;
`;

const DevUsage = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   white-space: nowrap;
   min-width: 130px;
   text-align: right;
`;

// ---- Link-vendo button + drawer (replaces the old native select) ----

const LinkVendoBtn = styled.button`
   margin-top: 4px;
   padding: 12px;
   border-radius: 8px;
   background: transparent;
   border: 1.5px dashed ${colors.GRAY12};
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: border-color 0.15s, background 0.15s;
   &:hover {
      border-color: ${colors.PRIMARY};
      background: ${colors.PRIMARY}08;
   }
   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      &:hover { border-color: ${colors.GRAY12}; background: transparent; }
   }
   span { color: ${colors.GRAY3}; font-weight: 400; margin-left: 4px; }
`;

const DrawerBackdrop = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0,0,0,0.4);
   z-index: 110;
   display: flex;
   justify-content: flex-end;
`;

const Drawer = styled.div`
   width: 380px;
   max-width: 100%;
   height: 100%;
   background: var(--app-surface, #fff);
   border-left: 1px solid ${colors.GRAY12};
   padding: 18px 20px;
   display: flex;
   flex-direction: column;
   gap: 12px;
   animation: slideIn 0.2s ease-out;
   @keyframes slideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
   }
`;

const DrawerHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
`;

const DrawerTitle = styled.h3`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const DrawerSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const DrawerList = styled.div`
   flex: 1 1 auto;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin: 4px -4px;
   padding: 0 4px;
`;

const DrawerRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   cursor: pointer;
   transition: background 0.15s, border-color 0.15s;
   &:hover { background: ${colors.GRAY14}; border-color: ${colors.PRIMARY}; }
`;

const Checkbox = styled.div<{ $checked: boolean }>`
   width: 18px;
   height: 18px;
   border-radius: 4px;
   border: 1.5px solid ${(p) => p.$checked ? colors.PRIMARY : colors.GRAY12};
   background: ${(p) => p.$checked ? colors.PRIMARY : 'transparent'};
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   transition: all 0.15s;
`;

const DrawerRowBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const DrawerRowName = styled.div`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
`;

const DrawerRowId = styled.div`
   font-size: 10px;
   color: ${colors.GRAY3};
   font-family: monospace;
`;

const DrawerFoot = styled.div`
   display: flex;
   gap: 8px;
   padding-top: 12px;
   border-top: 1px solid ${colors.GRAY12};
`;

const DrawerCancel = styled.button`
   flex: 1;
   padding: 9px;
   border-radius: 7px;
   background: transparent;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.GRAY3};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DrawerLink = styled.button`
   flex: 1;
   padding: 9px;
   border-radius: 7px;
   background: ${colors.PRIMARY};
   border: none;
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: opacity 0.15s;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ---- Rates panel ----

const RateList = styled.div`
   display: flex;
   flex-direction: column;
`;

const PlanRateTabBar = styled.div`
   display: flex;
   gap: 4px;
   margin-bottom: 8px;
   border-bottom: 1px solid ${colors.GRAY12};
`;

const PlanRateTab = styled.button<{ $active: boolean }>`
   border: none;
   background: transparent;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ $active }) => ($active ? colors.MAIN_DARK : colors.GRAY3)};
   border-bottom: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   display: flex;
   align-items: center;
   gap: 6px;
   margin-bottom: -1px;
   &:hover { color: ${colors.MAIN_DARK}; }
`;

const PlanRateTabCount = styled.span`
   font-size: 10px;
   background: ${colors.GRAY14};
   color: ${colors.GRAY3};
   padding: 1px 6px;
   border-radius: 8px;
   font-weight: 700;
`;

const RateHead = styled.div`
   display: flex;
   gap: 10px;
   padding: 6px 0;
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   border-bottom: 1px solid ${colors.GRAY12};
`;

const RateHeadCell = styled.div<{ $w?: string }>`
   width: ${(p) => p.$w || 'auto'};
   flex: ${(p) => p.$w ? 'unset' : 1};
`;

const RateRow = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
   padding: 9px 0;
   border-bottom: 1px solid ${colors.GRAY12};
`;

const ValidityCell = styled.div`
   width: 180px;
   flex: 0 0 180px;
`;

const RateCell = styled.div<{ $w?: string }>`
   width: ${(p) => p.$w || 'auto'};
   flex: ${(p) => p.$w ? 'unset' : 1};
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const RateRm = styled.button`
   font-size: 16px;
   color: ${colors.GRAY3};
   background: none;
   border: none;
   cursor: pointer;
   line-height: 1;
   padding: 2px 6px;
   border-radius: 4px;
   &:hover { color: ${colors.DANGER}; background: ${colors.DANGER}15; }
`;

// ---- Add forms ----

const AddBtn = styled.button`
   padding: 10px;
   border-radius: 7px;
   background: transparent;
   border: 1px dashed ${colors.GRAY12};
   color: ${colors.GRAY3};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   margin-top: 8px;
   &:hover { border-color: ${colors.PRIMARY}; color: ${colors.PRIMARY}; }
`;

const AddForm = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 12px;
   margin-top: 8px;
`;

const AddRow = styled.div`
   display: flex;
   gap: 8px;
   margin-bottom: 10px;
`;

const CompactInp = styled.input<{ $w?: string }>`
   width: ${(p) => p.$w || 'auto'};
   flex: ${(p) => p.$w ? 'unset' : 1};
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface, #fff);
   &:focus { outline: none; border-color: ${colors.PRIMARY}; }
`;

const AddBtnRow = styled.div`
   display: flex;
   gap: 8px;
   justify-content: flex-end;
`;

const AddCancel = styled.button`
   padding: 7px 14px;
   border-radius: 6px;
   background: transparent;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.GRAY3};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

const AddSubmit = styled.button`
   padding: 7px 18px;
   border-radius: 6px;
   background: ${colors.PRIMARY};
   border: none;
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: opacity 0.15s;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ---- Ledger panel ----

const LedgerList = styled.div`
   display: flex;
   flex-direction: column;
`;

const LedgerRow = styled.div`
   display: grid;
   grid-template-columns: 90px 90px 90px 1fr;
   gap: 10px;
   align-items: center;
   padding: 8px 0;
   border-bottom: 1px solid ${colors.GRAY12};
   font-size: 12px;
`;

const LedgerDate = styled.div`
   color: ${colors.GRAY3};
`;

const LedgerType = styled.div<{ $type: LedgerEntryType }>`
   font-size: 9px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   padding: 2px 8px;
   border-radius: 999px;
   text-align: center;
   background: ${(p) =>
      p.$type === 'base' ? `${colors.PRIMARY}15`
      : p.$type === 'topup' ? `${colors.SUCCESS}15`
      : `${colors.DANGER}15`};
   color: ${(p) =>
      p.$type === 'base' ? colors.PRIMARY
      : p.$type === 'topup' ? colors.SUCCESS
      : colors.DANGER};
`;

const LedgerAmount = styled.div<{ $negative: boolean }>`
   font-weight: 700;
   color: ${(p) => p.$negative ? colors.DANGER : colors.MAIN_DARK};
`;

const LedgerNote = styled.div`
   color: ${colors.GRAY3};
   font-size: 11px;
`;

const LedgerTypeRow = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 6px;
   margin-bottom: 10px;
`;

const LedgerTypeOpt = styled.button<{ $active: boolean }>`
   padding: 8px;
   border-radius: 6px;
   border: 1.5px solid ${(p) => p.$active ? colors.PRIMARY : colors.GRAY12};
   background: ${(p) => p.$active ? `${colors.PRIMARY}10` : 'transparent'};
   color: ${colors.MAIN_DARK};
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
`;
